import { useLingui } from "@lingui/react/macro";
import type { ResolvedKeybindingsConfig } from "@t3tools/contracts";
import { ChevronDownIcon, FolderIcon, TriangleAlertIcon } from "lucide-react";
import { memo, useEffect, useMemo, useState } from "react";
import { cn } from "~/lib/utils";
import type { Project } from "../../types";
import {
  buildProjectActionItems,
  filterCommandPaletteGroups,
  ITEM_ICON_CLASS,
  type CommandPaletteActionItem,
  type CommandPaletteGroup,
  type CommandPaletteSubmenuItem,
} from "../CommandPalette.logic";
import { CommandPaletteResults } from "../CommandPaletteResults";
import { ProjectFavicon } from "../ProjectFavicon";
import { badgeVariants } from "../ui/badge";
import { Command, CommandInput, CommandPanel } from "../ui/command";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";

interface DraftProjectChipProps {
  projects: ReadonlyArray<Project>;
  activeProject: Project | undefined;
  projectMissing: boolean;
  keybindings: ResolvedKeybindingsConfig;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectProject: (project: Project) => void;
}

/**
 * Header chip for a draft thread: shows the target project and opens a
 * searchable project picker. Prompts for a project while none is chosen and
 * warns when the draft's project no longer exists.
 */
export const DraftProjectChip = memo(function DraftProjectChip({
  projects,
  activeProject,
  projectMissing,
  keybindings,
  open,
  onOpenChange,
  onSelectProject,
}: DraftProjectChipProps) {
  const { t } = useLingui();
  const [query, setQuery] = useState("");
  const [highlightedItemValue, setHighlightedItemValue] = useState<string | null>(null);
  const unresolved = activeProject === undefined;

  useEffect(() => {
    if (!open) {
      setQuery("");
      setHighlightedItemValue(null);
    }
  }, [open]);

  const projectItems = useMemo(
    () =>
      buildProjectActionItems({
        projects,
        valuePrefix: "draft-project",
        icon: (project) => (
          <ProjectFavicon
            environmentId={project.environmentId}
            cwd={project.cwd}
            className={ITEM_ICON_CLASS}
          />
        ),
        runProject: async (project) => {
          onSelectProject(project);
        },
      }),
    [onSelectProject, projects],
  );

  const groups = useMemo<CommandPaletteGroup[]>(
    () =>
      filterCommandPaletteGroups({
        activeGroups: [{ value: "projects", label: t`Projects`, items: projectItems }],
        query,
        isInSubmenu: true,
        projectSearchItems: [],
        threadSearchItems: [],
      }),
    [projectItems, query, t],
  );

  const executeItem = (item: CommandPaletteActionItem | CommandPaletteSubmenuItem) => {
    if (item.kind !== "action" || item.disabled) {
      return;
    }
    void item.run();
  };

  const triggerLabel = activeProject
    ? activeProject.name
    : projectMissing
      ? t`Project not selected`
      : t`Choose a project`;
  const triggerTitle = activeProject
    ? t`Change the project for this draft`
    : projectMissing
      ? t`The project for this draft is unavailable. Pick a project to send.`
      : t`Pick the project this message is for.`;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger
        render={
          <button
            type="button"
            data-testid="draft-project-chip"
            data-project-unresolved={unresolved ? "true" : undefined}
            aria-label={unresolved ? t`Select a project` : t`Change project`}
            title={triggerTitle}
            className={cn(
              badgeVariants({ variant: projectMissing ? "warning" : "outline" }),
              "min-w-0 max-w-full shrink overflow-hidden pe-1 sm:max-w-56",
              projectMissing && "border-warning/40",
              unresolved && !projectMissing && "border-dashed text-muted-foreground",
            )}
          />
        }
      >
        {projectMissing ? (
          <TriangleAlertIcon className="text-warning" />
        ) : unresolved ? (
          <FolderIcon />
        ) : null}
        <span className="min-w-0 truncate">{triggerLabel}</span>
        <ChevronDownIcon className="opacity-60" />
      </PopoverTrigger>
      <PopoverPopup
        align="start"
        className="w-[min(28rem,calc(100vw-1.5rem))] overflow-hidden p-0"
        viewportClassName="p-0"
      >
        <Command
          aria-label={t`Select project`}
          autoHighlight="always"
          mode="none"
          value={query}
          onValueChange={setQuery}
          onItemHighlighted={(value) => {
            setHighlightedItemValue(typeof value === "string" ? value : null);
          }}
        >
          <CommandInput placeholder={t`Search projects...`} />
          <CommandPanel className="max-h-[min(20rem,50vh)] rounded-b-lg border-b">
            <CommandPaletteResults
              groups={groups}
              highlightedItemValue={highlightedItemValue}
              isActionsOnly={false}
              keybindings={keybindings}
              onExecuteItem={executeItem}
              emptyStateMessage={
                projects.length === 0 ? t`Add a project to start a chat.` : t`No matching projects.`
              }
            />
          </CommandPanel>
        </Command>
      </PopoverPopup>
    </Popover>
  );
});
